import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { franc } from 'franc';

/**
 * Shared response-actions (copy + speaker) for agents only.
 * Voice logic aligned with chatbot: franc language detection, preferred voice, segmented speech with pauses.
 */
@Component({
  selector: 'app-shared-response-actions',
  templateUrl: './response-actions.component.html',
  styleUrls: ['./response-actions.component.scss']
})
export class SharedResponseActionsComponent implements OnInit, OnDestroy {
  @Input() chatdata: any;
  @Input() ChatQueriesList: any[] = [];
  @Input() disabled = false;
  /** When false, hide copy button (per-agent config). Default true. */
  @Input() showCopy = true;
  /** When false, hide speaker/audio button (per-agent config). Default true. */
  @Input() showAudio = true;
  @Input() isAgent: boolean = false;
  @Input() renderedAnswerHtml: SafeHtml | string | null = null;

  @Output() listUpdate = new EventEmitter<any[]>();

  isAnswerCopied = false;
  private readonly synth = window.speechSynthesis;

  /**
   * Handles cross-tab speech cancellation.
   * The `storage` event only fires in OTHER browser tabs, so this naturally
   * avoids cancelling speech in the tab that initiated the new speech request.
   */
  private readonly onCrossTabSpeechCancel = (event: StorageEvent): void => {
    if (event.key === 'freya-speech-cancel') {
      try {
        this.synth.cancel();
        if (this.chatdata) {
          this.chatdata.isSpeaking = false;
          this.chatdata.isPaused = false;
        }
        const list = this.ChatQueriesList || [];
        list.forEach((item: any) => {
          item.isSpeaking = false;
          item.isPaused = false;
        });
        this.emitList(list);
        this.cdr.detectChanges();
      } catch (e) {
        // Cross-tab speech cancel failed silently
      }
    }
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly domSanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    window.addEventListener('storage', this.onCrossTabSpeechCancel);
  }

  ngOnDestroy(): void {
    this.stopSpeaking();
    window.removeEventListener('storage', this.onCrossTabSpeechCancel);
  }

  stripHtml(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  removeNonResponsiveCharacters(text: string): string {
    return text
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .trim();
  }

  /** Chatbot-style language detection (franc + JP/ZH regex). */
  detectLanguageFromText(text: string): 'en' | 'jp' | 'zh-CN' {
    const hasJapanese = /[\u3040-\u30FF\u31F0-\u31FF\uFF66-\uFF9D]/.test(text);
    if (hasJapanese) return 'jp';
    const hasChinese = /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(text);
    if (hasChinese) return 'zh-CN';
    const langCode = franc(text, { minLength: 5 });
    const map: { [key: string]: 'en' | 'jp' | 'zh-CN' } = {
      'eng': 'en', 'jpn': 'jp', 'cmn': 'zh-CN', 'zho': 'zh-CN'
    };
    return map[langCode] ?? 'en';
  }

  /** Chatbot-style preferred voice (female, language-specific). */
  getPreferredVoice(language: string): SpeechSynthesisVoice | null {
    const voices = this.synth.getVoices();
    if (!voices.length) return null;
    const langMap: { [key: string]: string } = {
      'en': 'en-US', 'jp': 'ja-JP', 'zh-CN': 'zh-CN'
    };
    const normalizedLang = langMap[language] || 'en-US';
    const preferredByLang: { [lang: string]: string[] } = {
      'en-US': ['Samantha', 'Zira', 'Google US English'],
      'ja-JP': ['Kyoko', 'Google 日本語'],
      'zh-CN': ['Ting-Ting', 'Mei-Jia', 'Google 普通话']
    };
    const names = preferredByLang[normalizedLang] || [];
    const preferred = voices.find(v =>
      v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()) &&
      names.some(n => v.name.toLowerCase().includes(n.toLowerCase()))
    );
    if (preferred) return preferred;
    const female = voices.find(v =>
      v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()) &&
      /female|samantha|zira|kyoko|ting|mei|mizuki|hana/.test(v.name.toLowerCase())
    );
    if (female) return female;
    const lang = voices.find(v => v.lang.toLowerCase().startsWith(normalizedLang.toLowerCase()));
    return lang || voices[0];
  }

  /** Chatbot-style processChatData: pauses, cleanup, TTS-friendly text. */
  processChatData(chatdata: any): string {
    if (!chatdata?.answer) return '';
    let t = (chatdata.answer + '')
      .replace(/(?:^|\s)(https?:\/\/|www\.|mailto:)[^\s]{0,200}/gim, '')
      .replace(/\[([^\]]{1,100})\]\((https?:\/\/[^\s)]{1,200})\)/g, '$1')
      .replace(/<br(?:\s{0,3}\/?)>(?:\s{0,3})<br(?:\s{0,3}\/?)>/gi, '[PAUSE_LONG]')
      .replace(/(\d{1,10})\.(?=\s)/g, '$1[PAUSE_SHORT]')
      .replace(/•\s{0,100}/g, '')
      .replace(/;/g, '[PAUSE_SHORT]')
      .replace(/:/g, '[PAUSE_SHORT]')
      .replace(/\betc\.\s*/gi, 'etecetra ')
      .replace(/\b(\w{1,50})\s+no\.\b\s*/gi, '$1 number ')
      .replace(/^#+\s*([^\n]{1,100})$/gm, '$1')
      .replace(/\be\.g\.\s*/gi, 'example ');
    t = this.stripHtml(t.replace(/\./g, '[PAUSE_LONG]'));
    return this.removeNonResponsiveCharacters(t);
  }

  /** Strip HTML tags by removing <...> spans. O(n), no regex backtracking. */
  private stripHtmlTags(s: string): string {
    let out = '';
    let i = 0;
    while (i < s.length) {
      const open = s.indexOf('<', i);
      if (open === -1) {
        out += s.slice(i);
        break;
      }
      out += s.slice(i, open);
      const close = s.indexOf('>', open + 1);
      i = close === -1 ? s.length : close + 1;
    }
    return out;
  }

  /** Unwrap **bold** markdown to plain text. O(n), no regex backtracking. */
  private unwrapBold(s: string): string {
    let out = '';
    let i = 0;
    const delim = '**';
    while (i < s.length) {
      const start = s.indexOf(delim, i);
      if (start === -1) {
        out += s.slice(i);
        break;
      }
      out += s.slice(i, start);
      const end = s.indexOf(delim, start + delim.length);
      if (end === -1) {
        out += s.slice(start);
        break;
      }
      out += s.slice(start + delim.length, end);
      i = end + delim.length;
    }
    return out;
  }

  /** Unwrap `code` markdown to plain text. O(n), no regex backtracking. */
  private unwrapBackticks(s: string): string {
    let out = '';
    let i = 0;
    const delim = '`';
    while (i < s.length) {
      const start = s.indexOf(delim, i);
      if (start === -1) {
        out += s.slice(i);
        break;
      }
      out += s.slice(i, start);
      const end = s.indexOf(delim, start + 1);
      if (end === -1) {
        out += s.slice(start);
        break;
      }
      out += s.slice(start + 1, end);
      i = end + 1;
    }
    return out;
  }

  getPlainText(): string {
    if (!this.chatdata?.answer) return '';
    let t = this.chatdata.answer.replace(/<br\s*\/?>/gi, '\n');
    t = this.stripHtmlTags(t);
    t = this.stripHtml(t);
    t = this.unwrapBold(t);
    t = this.unwrapBackticks(t);
    return this.formatMarkdownTablesInText(t.trim());
  }

  private formatMarkdownTablesInText(text: string): string {
    const lines = text.split(/\r?\n/);
    const output: string[] = [];
    let i = 0;

    while (i < lines.length) {
      if (this.isMarkdownTableHeader(lines[i])) {
        const separatorIndex = this.findNextNonEmptyLine(lines, i + 1);
        if (separatorIndex === -1 || !this.isMarkdownTableSeparator(lines[separatorIndex])) {
          output.push(lines[i]);
          i++;
          continue;
        }

        const tableBlock: string[] = [lines[i], lines[separatorIndex]];
        i = separatorIndex + 1;

        while (i < lines.length) {
          const currentLine = lines[i];
          if (!currentLine.trim()) {
            const nextNonEmptyIndex = this.findNextNonEmptyLine(lines, i + 1);
            if (nextNonEmptyIndex !== -1 && this.isPipeTableRow(lines[nextNonEmptyIndex])) {
              i = nextNonEmptyIndex;
              continue;
            }
            break;
          }

          if (this.isPipeTableRow(currentLine) || (tableBlock.length > 2 && currentLine.trim() !== '')) {
            tableBlock.push(currentLine);
            i++;
            continue;
          }
          break;
        }

        output.push(this.renderMarkdownTableBlock(tableBlock));
        continue;
      }

      output.push(lines[i]);
      i++;
    }

    return output.join('\n');
  }

  private findNextNonEmptyLine(lines: string[], fromIndex: number): number {
    for (let index = fromIndex; index < lines.length; index++) {
      if (lines[index].trim()) {
        return index;
      }
    }
    return -1;
  }

  private isMarkdownTableHeader(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.split('|').length >= 4;
  }

  private isMarkdownTableSeparator(line: string): boolean {
    const trimmed = line.trim();
    return /^\|(?:\s*:?-{3,}:?\s*\|)+$/.test(trimmed);
  }

  private isPipeTableRow(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|');
  }

  private getMarkdownTableColumnCount(line: string): number {
    const trimmed = line.trim();
    const noLeading = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
    const noBorders = noLeading.endsWith('|') ? noLeading.slice(0, -1) : noLeading;
    return noBorders.split('|').length;
  }

  private parseMarkdownTableRow(line: string, columnCount: number): string[] {
    const trimmed = line.trim();
    const noLeading = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
    const noBorders = noLeading.endsWith('|') ? noLeading.slice(0, -1) : noLeading;
    const cells = noBorders.split('|').map(cell => cell.trim());
    const normalized = new Array(columnCount).fill('');
    for (let idx = 0; idx < columnCount; idx++) {
      normalized[idx] = cells[idx] || '';
    }
    return normalized;
  }

  private renderMarkdownTableBlock(blockLines: string[]): string {
    if (blockLines.length < 2) {
      return blockLines.join('\n');
    }

    const columnCount = this.getMarkdownTableColumnCount(blockLines[0]);
    const header = this.parseMarkdownTableRow(blockLines[0], columnCount);
    const dataRows: string[][] = [];
    let activeRow: string[] | null = null;

    for (let idx = 2; idx < blockLines.length; idx++) {
      const line = blockLines[idx];
      if (this.isPipeTableRow(line)) {
        activeRow = this.parseMarkdownTableRow(line, columnCount);
        dataRows.push(activeRow);
      } else if (activeRow && line.trim()) {
        const lastColumn = columnCount - 1;
        activeRow[lastColumn] = activeRow[lastColumn]
          ? `${activeRow[lastColumn]}\n${line.trim()}`
          : line.trim();
      }
    }

    const rows = [header, ...dataRows];
    return rows
    .map(row => row.map(cell => this.normalizeTableCellWhitespace(cell)).join('\t'))
    .join('\n');
}

private normalizeTableCellWhitespace(cell: string): string {
  const normalizedSegments: string[] = [];
  for (const segment of cell.split('\n')) {
    const trimmed = segment.trim();
    if (trimmed) {
      normalizedSegments.push(trimmed);
    }
  }
  return normalizedSegments.join(' ');
}

  speakResponse(): void {
    if (!this.chatdata || this.disabled) return;
    if (!window.speechSynthesis) return;

    const list = this.ChatQueriesList || [];
    list.forEach((item: any) => {
      if (item !== this.chatdata) {
        item.isSpeaking = false;
        item.isPaused = false;
      }
    });

    if (this.chatdata.isSpeaking) {
      if (this.chatdata.isPaused) {
        this.synth.resume();
        this.chatdata.isPaused = false;
      } else {
        this.synth.pause();
        this.chatdata.isPaused = true;
      }
      this.emitList(list);
      return;
    }

    // Signal other browser tabs to cancel their speech so the OS engine is released
    try {
      localStorage.setItem('freya-speech-cancel', Date.now().toString());
    } catch (e) {}

    this.synth.cancel();
    const textWithMarkers = this.processChatData(this.chatdata);
    if (!textWithMarkers.trim()) return;

    const segments = textWithMarkers.split(/(\[PAUSE_SHORT\]|\[PAUSE_LONG\])/);
    const chatdata = this.chatdata;

    const speakSegments = (index = 0) => {
      if (index >= segments.length) {
        chatdata.isSpeaking = false;
        chatdata.isPaused = false;
        this.emitList(list);
        this.cdr.detectChanges();
        return;
      }
      const segment = segments[index];
      let delay = 0;
      if (segment === '[PAUSE_SHORT]') delay = 800;
      else if (segment === '[PAUSE_LONG]') delay = 1000;
      else if (segment.trim()) {
        const lang = this.detectLanguageFromText(segment.trim());
        const langMap: { [key: string]: string } = { 'en': 'en-US', 'jp': 'ja-JP', 'zh-CN': 'zh-CN' };
        const voice = this.getPreferredVoice(lang);
        if (!voice) {
          speakSegments(index + 1);
          return;
        }
        const utterance = new SpeechSynthesisUtterance(segment.trim());
        utterance.voice = voice;
        utterance.lang = langMap[lang] || 'en-US';
        utterance.pitch = 1.1;
        utterance.rate = 0.9;
        utterance.volume = 0.9;
        utterance.onstart = () => {
          chatdata.isSpeaking = true;
          chatdata.isPaused = false;
          list.forEach((item: any) => { if (item !== chatdata) item.isSpeaking = false; item.isPaused = false; });
          this.emitList(list);
          this.cdr.detectChanges();
        };
        utterance.onend = () => setTimeout(() => speakSegments(index + 1), 0);
        this.synth.speak(utterance);
        return;
      }
      setTimeout(() => speakSegments(index + 1), delay);
    };

    // Delay after cancel() to let the OS speech engine fully release —
    // calling speak() immediately after cancel() silently fails in Chrome/Edge
    setTimeout(() => {
      speakSegments();
      this.emitList(list);
      this.cdr.detectChanges();
    }, 200);
  }

  private emitList(list: any[]): void {
    this.listUpdate.emit([...list]);
  }

  stopSpeaking(): void {
    this.synth.cancel();
    const list = this.ChatQueriesList || [];
    list.forEach((item: any) => {
      item.isSpeaking = false;
      item.isPaused = false;
    });
    if (this.chatdata) {
      this.chatdata.isSpeaking = false;
      this.chatdata.isPaused = false;
    }
    this.emitList(list);
    this.cdr.detectChanges();
  }

  private getRenderedHtmlForCopy(): string {
    if (!this.renderedAnswerHtml) {
      return '';
    }
    return this.domSanitizer.sanitize(SecurityContext.HTML, this.renderedAnswerHtml) || '';
  }

  private getPlainTextFromRenderedHtml(renderedHtml: string): string {
    const container = document.createElement('div');
    container.innerHTML = renderedHtml.replace(/<br\s*\/?>/gi, '\n');
    return (container.innerText || container.textContent || '').trim();
  }

  private async writeToClipboard(plainText: string, renderedHtml: string): Promise<void> {
    if (!navigator.clipboard) {
      throw new Error('Clipboard API is not supported');
    }

    const canWriteHtml = !!renderedHtml && typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function';
    if (canWriteHtml) {
      try {
        const htmlBlob = new Blob([renderedHtml], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        const item = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        await navigator.clipboard.write([item]);
        return;
      } catch {
        // Fallback to plain text clipboard.
      }
    }

    if (typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(plainText);
      return;
    }

    throw new Error('Clipboard API writeText is not supported');
  }

  async copyToClipboard(): Promise<void> {
    if (!this.chatdata || this.disabled) return;
    const renderedHtml = this.getRenderedHtmlForCopy();
    const text = renderedHtml ? this.getPlainTextFromRenderedHtml(renderedHtml) : this.getPlainText();
    if (!text) return;
    this.writeToClipboard(text, renderedHtml).then(() => {
      this.isAnswerCopied = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.isAnswerCopied = false;
        this.cdr.detectChanges();
      }, 2000);
    }).catch(() => {});
  }
}
